import { useState } from "react";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SignatureForm from "@/components/signature/SignatureForm";
import SignaturePreview from "@/components/signature/SignaturePreview";
import { generateSignatureHtml } from "@/components/signature/generateSignatureHtml";

const Signature = () => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "Maxim Rousseau",
    title: "Fondateur",
    phone: "819-460-9725",
    email: "maxim@agence8020.com",
    calUrl: "https://cal.com/maxim-rousseau-6ciolz/30min-suivi",
    photoUrl: "",
    photoPreview: "",
  });

  const signatureHtml = generateSignatureHtml(formData);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(signatureHtml);
      setCopied(true);
      toast({
        title: "Copié!",
        description: "La signature HTML a été copiée dans le presse-papier.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de copier la signature.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <SEOHead
        title="Générateur de Signature Email | Agence 8020"
        description="Page interne - Générateur de signature email HTML pour l'équipe Agence 8020."
        canonical="/signature"
        noIndex={true}
      />
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Générateur de Signature Email</h1>
          <p className="text-muted-foreground mb-8">
            Remplissez les champs ci-dessous pour générer automatiquement votre signature email HTML.
          </p>

          <SignatureForm formData={formData} onChange={setFormData} />

          <div className="mt-6">
            <SignaturePreview {...formData} />
          </div>

          <div className="bg-muted rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Code HTML</h2>
              <Button onClick={copyToClipboard} variant="outline" size="sm">
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copié!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copier le code
                  </>
                )}
              </Button>
            </div>
            <pre className="bg-background p-4 rounded-md overflow-x-auto text-sm max-h-64 overflow-y-auto">
              <code>{signatureHtml}</code>
            </pre>
          </div>

          <div className="mt-8 p-6 bg-muted/50 rounded-lg">
            <h3 className="font-semibold mb-3">📝 Instructions</h3>
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li>Remplis tous les champs ci-dessus (nom, titre, photo, etc.)</li>
              <li>Héberge la photo de profil en ligne (ex: dans le dossier <code>/public/signature/</code>)</li>
              <li>Remplace l'URL de la photo dans le code HTML par l'URL publique</li>
              <li>Copiez le code HTML et collez-le dans les paramètres de signature de votre client email</li>
            </ol>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signature;
